import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, BadgeCheck, MessageCircle, ShieldCheck, Sparkles } from "lucide-react";
import { API, CATEGORIES, CAT_ICONS, useSite } from "@/site/common";
import { SearchBox } from "@/site/SearchBox";
import { CardSkeleton, ListingCard } from "@/site/ListingCard";

export const HERO_IMG = "https://images.unsplash.com/photo-1557868969-df9517e8df10?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";
export const ROOM_IMG = "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";
export const KEYS_IMG = "https://images.unsplash.com/photo-1741156386380-0236c72eb6f9?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

function Hero() {
  const s = useSite();
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-orange-50/70 via-white to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-7 relative z-10 site-rise">
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-orange-200 text-orange-700 text-xs font-bold uppercase tracking-[.18em] px-3.5 py-1.5"><Sparkles size={13} />Emlak Sepeti</span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.035em] leading-[1.05] text-slate-900" data-testid="home-hero-title">{s.hero_title || "Hayalinizdeki evi birlikte bulalım"}</h1>
          <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">{s.hero_text}</p>
          <div className="mt-9 lg:-mr-28"><SearchBox /></div>
        </div>
        <div className="lg:col-span-5 relative site-rise" style={{ animationDelay: "120ms" }}>
          <div className="relative rounded-[32px] overflow-hidden aspect-[4/5] shadow-[0_40px_80px_-40px_rgba(15,23,42,.6)]"><img src={HERO_IMG} alt="Modern konut" className="h-full w-full object-cover" /></div>
          <div className="absolute -right-5 top-10 hidden sm:flex items-center gap-3 bg-white rounded-2xl px-4 py-3.5 shadow-xl border border-slate-100 site-float">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-orange-500 text-white"><BadgeCheck size={22} /></span>
            <div><p className="text-xl font-extrabold text-slate-900 leading-none" data-testid="home-active-count">{s.stats?.active ?? "–"}</p><p className="text-xs text-slate-500 mt-1">aktif ilan</p></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Categories() {
  const s = useSite();
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {Object.entries(CATEGORIES).map(([k, label], i) => { const Icon = CAT_ICONS[k]; return (
          <Link key={k} to={`/ilanlar?category=${k}`} className="group flex flex-col items-start gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 hover:border-orange-300 hover:bg-orange-50/50 transition-colors duration-200 site-rise" style={{ animationDelay: `${i * 40}ms` }} data-testid={`home-category-${k}`}>
            <span className="grid place-items-center w-10 h-10 rounded-xl bg-slate-100 text-slate-700 group-hover:bg-orange-500 group-hover:text-white transition-colors duration-200"><Icon size={19} /></span>
            <span><span className="block text-sm font-bold text-slate-800">{label}</span><span className="text-xs text-slate-400">{s.counts?.[k] || 0} ilan</span></span>
          </Link>
        ); })}
      </div>
    </section>
  );
}

function Latest() {
  const s = useSite();
  const [items, setItems] = useState(null);
  useEffect(() => { axios.get(`${API}/site/listings`, { params: { per: 6 } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="home-latest">
      <div className="flex items-end justify-between gap-6 mb-9">
        <div><p className="text-xs font-bold uppercase tracking-[.2em] text-orange-600">Portföy</p><h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Yeni ve öne çıkan ilanlar</h2></div>
        <Link to="/ilanlar" className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-slate-900 hover:text-orange-600 transition-colors" data-testid="home-all-listings">Tüm ilanlar <ArrowRight size={16} /></Link>
      </div>
      {items === null ? <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <CardSkeleton key={i} />)}</div>
        : items.length ? <div className="grid gap-6 sm:gap-8 sm:grid-cols-2 lg:grid-cols-3">{items.map((l, i) => <ListingCard key={l.id} l={l} i={i} />)}</div>
        : <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center" data-testid="home-empty">
            <p className="text-lg font-bold text-slate-800">Yeni ilanlarımız çok yakında burada</p>
            <p className="mt-2 text-sm text-slate-500">İlk siz haberdar olmak için WhatsApp kanalımızı takip edin.</p>
            {s.whatsapp_channel && <a href={s.whatsapp_channel} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-5 py-3 transition-colors"><MessageCircle size={17} />Kanalı takip et</a>}
          </div>}
    </section>
  );
}

function SellBand() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="relative overflow-hidden rounded-[32px] bg-slate-900 text-white grid md:grid-cols-2">
        <div className="p-8 sm:p-12 lg:p-16 relative z-10">
          <p className="text-xs font-bold uppercase tracking-[.2em] text-orange-400">Ev sahipleri için</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Evinizi değerinde satalım ya da kiralayalım</h2>
          <p className="mt-4 text-slate-300 leading-relaxed max-w-md">Mülkünüzü birkaç dakikada bize bildirin; danışmanımız sizi arayıp ücretsiz değerleme ve fotoğraf çekimini planlasın.</p>
          <ul className="mt-6 space-y-2.5 text-sm text-slate-200">{["Ücretsiz yerinde değerleme", "Profesyonel ilan ve fotoğraf", "Instagram ve WhatsApp kanalımızda tanıtım"].map((t) => <li key={t} className="flex items-center gap-2.5"><ShieldCheck size={16} className="text-orange-400" />{t}</li>)}</ul>
          <Link to="/evinizi-satalim" className="mt-9 inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3.5 transition-[background-color,transform] duration-200 hover:-translate-y-0.5" data-testid="home-sell-cta">Mülkümü bildir <ArrowRight size={17} /></Link>
        </div>
        <div className="relative min-h-[260px]"><img src={KEYS_IMG} alt="Ev anahtarı" className="absolute inset-0 h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/20 to-transparent md:block hidden" /></div>
      </div>
    </section>
  );
}

function AboutTeaser() {
  const s = useSite();
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 grid lg:grid-cols-2 gap-12 items-center">
      <div className="rounded-[28px] overflow-hidden aspect-[5/4]"><img src={ROOM_IMG} alt="Aydınlık salon" loading="lazy" className="h-full w-full object-cover" /></div>
      <div>
        <p className="text-xs font-bold uppercase tracking-[.2em] text-orange-600">Hakkımızda</p>
        <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">{s.about_title}</h2>
        <p className="mt-5 text-slate-600 leading-relaxed line-clamp-5 whitespace-pre-line">{s.about_text}</p>
        <div className="mt-8 grid grid-cols-2 gap-4 max-w-sm">
          <div className="rounded-2xl bg-slate-50 p-5"><p className="text-3xl font-extrabold text-slate-900">{s.stats?.active ?? 0}</p><p className="text-xs text-slate-500 mt-1">aktif ilan</p></div>
          <div className="rounded-2xl bg-orange-50 p-5"><p className="text-3xl font-extrabold text-orange-600">{s.stats?.closed ?? 0}</p><p className="text-xs text-slate-500 mt-1">satılan / kiralanan</p></div>
        </div>
        <Link to="/hakkimizda" className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-slate-900 hover:text-orange-600 transition-colors" data-testid="home-about-link">Bizi tanıyın <ArrowRight size={16} /></Link>
      </div>
    </section>
  );
}

export default function Home() {
  const s = useSite();
  useEffect(() => { document.title = `${s.name || "Emlak Sepeti"} · Satılık ve kiralık ilanlar`; }, [s.name]);
  return <div data-testid="site-home"><Hero /><Categories /><Latest /><SellBand /><AboutTeaser /></div>;
}
